import type { TenderRate } from '@centric-os/types';
import { DateTime, Interval } from 'luxon';

/**
 * Provides utility functions related to Meal Equivalency (MEQ) tender validation.
 *
 * @returns {{
 *   validateMealEquivalencyTenderTimeRanges: (rates: TenderRate[]) => { isValid: boolean, error?: string }
 * }}
 */
export default () => {
  /**
   * Validates that MEQ tender time ranges cover a full 24 hours and don't overlap.
   * Uses Luxon intervals for efficient time range operations.
   *
   * @param {TenderRate[]} rates - The array of tender rates to validate
   * @returns {{ isValid: boolean, error? string }} Validation result with error details if invalid
   */
  const validateMealEquivalencyTenderTimeRanges = (
    rates: TenderRate[],
  ): {
    isValid: boolean;
    error?: string;
  } => {
    if (!rates?.length) {
      return { isValid: false, error: 'missingRanges' };
    }

    // Create a baseline date to use as reference (actual date doesn't matter, just need consistent times)
    const baselineDate = DateTime.now().startOf('day');

    // Convert each rate to a Luxon Interval
    const intervals = rates.flatMap((rate) => {
      if (!rate.startTime || !rate.endTime) {
        return [];
      }

      // Extract hours and minutes from the rate times
      const startDate = new Date(rate.startTime);
      const endDate = new Date(rate.endTime);

      // Create DateTime objects using the baseline date but with the hours/minutes from the rates
      let start = baselineDate.set({
        hour: startDate.getHours(),
        minute: startDate.getMinutes(),
      });

      let end = baselineDate.set({
        hour: endDate.getHours(),
        minute: endDate.getMinutes(),
      });

      // If end <= start, shift it by one day (handles overnight ranges)
      if (end <= start) {
        end = end.plus({ days: 1 });
      }

      return Interval.fromDateTimes(start, end);
    });

    if (!intervals.length) {
      return { isValid: false, error: 'missingRanges' };
    }

    const totalDuration = intervals.reduce((acc, interval) => acc + interval.length('minutes'), 0);
    if (totalDuration > 1440) {
      return { isValid: false, error: 'overlappingRanges' };
    }

    // Merge all intervals into a minimal set
    const merged = Interval.merge(intervals);

    // If there's more than one interval after merging, we have gaps
    if (merged.length > 1) {
      return { isValid: false, error: 'incompleteCoverage' };
    }

    // Check if we have exactly 24 hours coverage
    if (totalDuration < 1440) {
      return { isValid: false, error: 'incompleteCoverage' };
    }

    return { isValid: true };
  };

  return {
    validateMealEquivalencyTenderTimeRanges,
  };
};
